import { httpTokenCommon, httpTokenFileDownload } from "../helper/http-common";
import { paymentsWalletType } from "../types/invoice&payment";

const extractInvoiceRows = (data: unknown): unknown[] => {
  if (Array.isArray(data)) return data;
  const paged = data as { items?: unknown[] } | null | undefined;
  if (Array.isArray(paged?.items)) return paged.items;
  return [];
};

class InvoiceService {
  getAllInvoices(options?: { silent?: boolean }) {
    return httpTokenCommon.get("/invoices", { silent: options?.silent });
  }

  getInvoiceList(
    params: { keyword?: string; pageNumber?: number; pageSize?: number; sortDescending?: boolean },
    options?: { silent?: boolean }
  ) {
    return httpTokenCommon.get("/invoices/search", { params, silent: options?.silent });
  }

  /**
   * Loads invoices for the portal without surfacing transient API errors.
   * Tries /invoices/search first (mobile parity), then GET /invoices as fallback.
   */
  async fetchInvoicesForPortal(): Promise<unknown[]> {
    const silent = { silent: true } as const;
    const searchAttempts = [
      { keyword: "", pageNumber: 1, pageSize: 200, sortDescending: true },
      { keyword: "", pageNumber: 1, pageSize: 50, sortDescending: true },
    ];

    for (const params of searchAttempts) {
      try {
        const response = await this.getInvoiceList(params, silent);
        return extractInvoiceRows(response.data);
      } catch {
        // try next strategy
      }
    }

    try {
      const response = await this.getAllInvoices(silent);
      return extractInvoiceRows(response.data);
    } catch {
      return [];
    }
  }

  async PaymentCreate(inputData: paymentsWalletType) {
    try {
      return await httpTokenCommon.post("/payments/create", inputData);
    } catch (err: unknown) {
      // Backend returns 409 when a payment for this order already exists
      const axiosErr = err as { response?: { status?: number; data?: Record<string, unknown> } };
      if (axiosErr?.response?.status === 409) {
        const d = axiosErr.response?.data ?? {};
        return {
          ...axiosErr.response,
          status: 200,
          data: {
            transactionId: d.existingTransactionId,
            type: d.existingType,
            amount: d.existingAmount,
            paymentStatus: d.existingStatus,
            isDuplicate: true,
          },
        };
      }
      throw err;
    }
  }

  getStatusDetailsData(
    type: string,
    transactionId: string,
    options?: { silent?: boolean }
  ) {
    const encodedType = encodeURIComponent(String(type).trim());
    const encodedId = encodeURIComponent(String(transactionId).trim());
    return httpTokenCommon.get(`/payments/status/${encodedType}/${encodedId}`, {
      silent: options?.silent,
    });
  }

  getReceptDetailsData(invoiceId: string) {
    return httpTokenCommon.get(`/invoices/getreceipt?invoiceId=${invoiceId}`);
  }

  getPaymentsForInvoice(invoiceId: string) {
    return httpTokenCommon.get(`/payments`, { params: { invoiceId } });
  }

  getAllPayments(options?: { silent?: boolean }) {
    return httpTokenCommon.get(`/payments`, { silent: options?.silent });
  }

  downloadReceiptData(invoiceId: string) {
    return httpTokenFileDownload.get(`/invoices/downloadreceipt?invoiceId=${invoiceId}`);
  }

  getDashboardData() {
    return httpTokenCommon.get(`/invoices/dashboard`);
  }

  WalletPaymentCreate(inputData: paymentsWalletType) {
    return httpTokenCommon.post("/payments/recharge", inputData);
  }

  getOrderPaymentSummary(orderId: string) {
    return httpTokenCommon.get(`/payments/orders/${orderId}/summary`);
  }

  async payOrder(
    orderId: string,
    body: {
      amount?: number;
      payFullRemaining?: boolean;
      method: string;
      redirectUrl?: string;
      successUrl?: string;
      failedUrl?: string;
      language?: string;
      externalReferenceId?: string;
    }
  ) {
    try {
      return await httpTokenCommon.post(`/payments/orders/${orderId}/pay`, body);
    } catch (err: unknown) {
      const axiosErr = err as {
        response?: { status?: number; data?: Record<string, unknown> };
      };
      if (axiosErr?.response?.status === 409) {
        const d = axiosErr.response?.data ?? {};
        return {
          ...axiosErr.response,
          status: 200,
          data: {
            transactionId: d.existingTransactionId ?? d.pendingTransactionId,
            type: d.existingType,
            amount: d.existingAmount,
            paymentStatus: d.existingStatus,
            isDuplicate: true,
            error: d.error,
            remainingAmount: d.remainingAmount,
          },
        };
      }
      throw err;
    }
  }

  createCheckoutInvoice(dto: {
    invoiceNumber: string;
    orderNumber: string;
    dueDate: string;
    subtotal: number;
    tax: string;
    total: number;
  }) {
    return httpTokenCommon.post(
      "/invoices",
      { dto: { ...dto, amountDue: dto.total } },
      { silent: true }
    );
  }
}

const invoiceService = new InvoiceService();
export default invoiceService;
